import { balance } from "./types.ts";

// LocalController owns the local player's position, input state, and
// throttled outbound move dispatch. Remote players are tracked separately
// in RemoteInterpolator below because the state shape is different.
export class LocalController {
  // Explicit `number` types: balance values are literal-narrowed via
  // `as const`, and these fields get reassigned with arbitrary numbers.
  x: number = balance.world.defaultSpawnX;
  y: number = balance.world.defaultSpawnY;
  private keys = new Set<string>();
  private lastSentAt = 0;
  private lastSentX = Number.NaN;
  private lastSentY = Number.NaN;
  private _modalOpen = false;

  constructor(private readonly sendMove: (x: number, y: number, t: number) => void) {}

  attach(target: Window): void {
    target.addEventListener("keydown", (e: KeyboardEvent) => {
      if (this.isMovementKey(e.key)) {
        this.keys.add(e.key.toLowerCase());
        e.preventDefault();
      }
    });
    target.addEventListener("keyup", (e: KeyboardEvent) => {
      if (this.isMovementKey(e.key)) {
        this.keys.delete(e.key.toLowerCase());
        e.preventDefault();
      }
    });
    // Release all keys on blur so the player doesn't slide forever when
    // alt-tabbed away.
    target.addEventListener("blur", () => this.keys.clear());
  }

  setPosition(x: number, y: number): void {
    this.x = x;
    this.y = y;
  }

  // Suppress movement while a modal (e.g. workbench panel) is open.
  // Clears held keys on open so a key held through the modal doesn't
  // ghost-move the player after close.
  setModal(open: boolean): void {
    if (open) this.keys.clear();
    this._modalOpen = open;
  }

  // Advance local position by dtSeconds. Client-authoritative for Phase 1.
  tick(dtSeconds: number, now: number): void {
    if (this._modalOpen) return;
    let dx = 0;
    let dy = 0;
    if (this.keys.has("w") || this.keys.has("arrowup")) dy -= 1;
    if (this.keys.has("s") || this.keys.has("arrowdown")) dy += 1;
    if (this.keys.has("a") || this.keys.has("arrowleft")) dx -= 1;
    if (this.keys.has("d") || this.keys.has("arrowright")) dx += 1;

    if (dx !== 0 || dy !== 0) {
      // Normalize diagonal speed so W+D isn't 1.41x faster than W alone.
      const len = Math.hypot(dx, dy);
      dx /= len;
      dy /= len;
      const step = balance.movement.speedPxPerSec * dtSeconds;
      const halfSize = balance.player.sizePx / 2; // contracts:allow-literal — centering idiom
      this.x = clamp(this.x + dx * step, halfSize, balance.world.widthPx - halfSize);
      this.y = clamp(this.y + dy * step, halfSize, balance.world.heightPx - halfSize);
    }

    // Throttle outbound at 20 Hz AND only if position actually changed.
    // Skipping unchanged frames matters: held-key idle shouldn't spam.
    if (
      now - this.lastSentAt >= balance.movement.clientSendIntervalMs &&
      (this.x !== this.lastSentX || this.y !== this.lastSentY)
    ) {
      this.sendMove(this.x, this.y, now);
      this.lastSentAt = now;
      this.lastSentX = this.x;
      this.lastSentY = this.y;
    }
  }

  private isMovementKey(key: string): boolean {
    const k = key.toLowerCase();
    return (
      k === "w" ||
      k === "a" ||
      k === "s" ||
      k === "d" ||
      k === "arrowup" ||
      k === "arrowdown" ||
      k === "arrowleft" ||
      k === "arrowright"
    );
  }
}

// For each remote player we keep the previous authoritative position and
// the most-recently-received one, and lerp between them over the packet
// interval. This hides jitter and never snaps, per the spec.
type Interp = {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  startedAt: number;
  durationMs: number;
};

export class RemoteInterpolator {
  private readonly state = new Map<string, Interp>();

  seed(playerId: string, x: number, y: number, now: number): void {
    this.state.set(playerId, {
      fromX: x,
      fromY: y,
      toX: x,
      toY: y,
      startedAt: now,
      durationMs: balance.movement.clientSendIntervalMs,
    });
  }

  target(playerId: string, x: number, y: number, now: number): void {
    const existing = this.state.get(playerId);
    if (!existing) {
      this.seed(playerId, x, y, now);
      return;
    }
    // Start next lerp from where the current one visually is, not from
    // the stale `toX/toY` — guards against hitches if updates land late.
    const sample = this.sampleInterp(existing, now);
    this.state.set(playerId, {
      fromX: sample.x,
      fromY: sample.y,
      toX: x,
      toY: y,
      startedAt: now,
      durationMs: balance.movement.clientSendIntervalMs,
    });
  }

  drop(playerId: string): void {
    this.state.delete(playerId);
  }

  sample(playerId: string, now: number): { x: number; y: number } | undefined {
    const entry = this.state.get(playerId);
    if (!entry) return undefined;
    return this.sampleInterp(entry, now);
  }

  private sampleInterp(entry: Interp, now: number): { x: number; y: number } {
    const elapsed = now - entry.startedAt;
    const t = entry.durationMs <= 0 ? 1 : Math.min(1, elapsed / entry.durationMs);
    return {
      x: entry.fromX + (entry.toX - entry.fromX) * t,
      y: entry.fromY + (entry.toY - entry.fromY) * t,
    };
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
